import MojitoCore from 'mojito/core';
import KeyCodes from 'modules/keypad/key-codes.js';
import ClassNames from 'modules/keypad/class-names.js';
import MojitoPresentation from 'mojito/presentation';
import { KEYS, LAYOUT } from 'modules/keypad/types.js';
import KeypadImageButton from 'modules/keypad/numeric-keypad/keypad-image-button/index.js';
import IncrementalKeypad from 'modules/keypad/numeric-keypad/incremental-view/index.js';

const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const { FlexPane, Button, AbsolutePane } = MojitoPresentation.Components;
const { CurrencyHelper } = MojitoPresentation.Utils;
const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;
const { resizeDetector } = MojitoCore.Base.resizeDetector;

export default class NumericKeypad extends UIViewImplementation {
    constructor(component) {
        super(component);

        this.state = {
            scrollWidth: undefined,
        };

        this.preventEvent = this.preventEvent.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onRefChange = this.onRefChange.bind(this);
        this.onClick = this.onClick.bind(this);
    }

    getLayout() {
        return this.state.scrollWidth > this.config.narrowMaxWidth ? LAYOUT.WIDE : LAYOUT.NARROW;
    }

    componentDidMount() {
        if (this.config.enableResponsiveLayout && this.keypadElement) {
            resizeDetector.listen(this.keypadElement, this.onResize);
        }
    }

    componentWillUnmount() {
        if (this.keypadElement) {
            resizeDetector.unlisten(this.keypadElement);
        }
    }

    onRefChange(el) {
        this.keypadElement = el;
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(this.props, nextProps) || !isShallowEqual(this.state, nextState);
    }

    onResize(element) {
        if (element.scrollWidth !== this.state.scrollWidth) {
            this.setState({ scrollWidth: element.scrollWidth });
        }
    }

    preventEvent(event) {
        event.nativeEvent.stopImmediatePropagation();
    }

    renderKey(name, value) {
        const clickData = { type: KeyCodes.KEY, value };
        const config = this.style.buttons[name];
        return this.renderButton({ config, value, clickData, taClass: `ta-key-${name}` });
    }

    renderEmptyKey() {
        return <FlexPane config={this.config.emptyKey} class={'ta-key-empty'} />;
    }

    renderButton({ config, value, clickData, taClass }) {
        return (
            <Button
                config={config}
                onClickData={clickData}
                onClick={this.onClick}
                class={`${ClassNames.KEYPAD_BUTTON} ${taClass}`}
            >
                {value}
            </Button>
        );
    }

    onClick(event, data) {
        this.preventEvent(event);
        this.props.onClick(data);
        if (data.type === KeyCodes.INCREMENT) {
            this.emitAnalytics('customKeypadKeyClicked', {
                keyCode: data.type,
                keyName: data.keyName,
                value: data.value,
            });
        }
    }

    renderKeys() {
        const layout = this.getLayout();
        const container = this.style.layout.keysContainer;

        return (
            <FlexPane config={container[layout]}>
                {this.renderLeft()}
                {this.renderRight()}
            </FlexPane>
        );
    }

    renderLeft() {
        return (
            <FlexPane config={this.style.layout.leftContainer}>
                <FlexPane config={this.style.layout.rowContainer}>
                    {this.renderKey('key1', '1')}
                    {this.renderKey('key2', '2')}
                </FlexPane>
                <FlexPane config={this.style.layout.rowContainer}>
                    {this.renderKey('key4', '4')}
                    {this.renderKey('key5', '5')}
                </FlexPane>
                <FlexPane config={this.style.layout.rowContainer}>
                    {this.renderKey('key7', '7')}
                    {this.renderKey('key8', '8')}
                </FlexPane>
                <FlexPane config={this.style.layout.rowContainer}>
                    {this.renderKey('key0', '0')}
                </FlexPane>
            </FlexPane>
        );
    }

    renderRight() {
        return (
            <FlexPane config={this.style.layout.rightContainer}>
                <FlexPane config={this.style.layout.rightColumnContainer}>
                    {this.renderKey('key3', '3')}
                    {this.renderKey('key6', '6')}
                    {this.renderKey('key9', '9')}
                    {this.config.showDotKey
                        ? this.renderKey(
                              'dot',
                              CurrencyHelper.getCurrencyDecimalSeparator(this.props.currencyCode)
                          )
                        : this.renderEmptyKey()}
                </FlexPane>
                <FlexPane config={this.style.layout.rightColumnContainer}>
                    <KeypadImageButton
                        onClick={this.onClick}
                        config={this.config.keypadImageEraseButton}
                        onClickData={{ type: KeyCodes.KEY, value: 'Backspace' }}
                        class={`${ClassNames.KEYPAD_BUTTON} ta-key-erase`}
                    />
                    <Button
                        onClick={this.onClick}
                        onClickData={{ type: KeyCodes.ENTER }}
                        config={this.style.buttons.done}
                        class={`${ClassNames.KEYPAD_BUTTON} ta-key-done`}
                    >
                        {this.resolveString('$NUMERIC_KEYPAD.DONE')}
                    </Button>
                </FlexPane>
            </FlexPane>
        );
    }

    render() {
        const layout = this.getLayout();
        const incrementalNumericKeypadConfigsMap = {
            [LAYOUT.WIDE]: this.config.incrementalNumericKeypadWide,
            [LAYOUT.NARROW]: this.config.incrementalNumericKeypadNarrow,
        };

        return (
            <div
                ref={this.onRefChange}
                onClick={this.preventEvent}
                style={this.style.layout.containerWrapper}
            >
                <FlexPane config={this.style.container[layout]} class="ta-NumericKeypad">
                    <IncrementalKeypad
                        config={incrementalNumericKeypadConfigsMap[layout]}
                        onClick={this.props.onClick}
                    />
                    {this.renderKeys()}
                </FlexPane>
                {!this.props.clickable && (
                    <AbsolutePane class="KeypadButton" config={this.config.unclickableOverlay} />
                )}
            </div>
        );
    }
}

NumericKeypad.getStyle = function (config, mode, merge) {
    const style = {};
    const { itemSpacing, container: configContainer } = config;

    const mainContainer = merge(
        {
            itemSpacing,
            style: {
                flexBasis: '0%',
                flexGrow: '0',
                flexShrink: '0',
            },
        },
        configContainer
    );

    const { userSelect } = configContainer.style;

    if (userSelect) {
        mainContainer.style.userSelect = userSelect;
    }

    const columnDirection = { flexDirection: 'column' };
    const rowDirection = { flexDirection: 'row' };

    style.container = {
        [LAYOUT.NARROW]: merge(mainContainer, { style: columnDirection }),
        [LAYOUT.WIDE]: merge(mainContainer, { style: rowDirection }),
    };

    const basisContainer = {
        itemSpacing,
        style: {
            flexBasis: '0%',
        },
    };

    style.layout = {
        containerWrapper: {
            // zIndex in style fixes box shadow for mobile devices
            ...config.style,
            position: 'relative',
            top: 0,
        },

        rowContainer: basisContainer,

        keysContainer: {
            [LAYOUT.NARROW]: merge(basisContainer, { style: { flexGrow: 1 } }),
            [LAYOUT.WIDE]: merge(basisContainer, { style: { flexGrow: 4 } }),
        },

        leftContainer: {
            itemSpacing,
            style: {
                flexDirection: 'column',
                flexGrow: 2,
                flexBasis: '0%',
            },
        },

        rightContainer: {
            itemSpacing,
            style: {
                flexDirection: 'row',
                flexGrow: 2,
                flexBasis: '0%',
            },
        },

        rightColumnContainer: {
            itemSpacing,
            style: {
                flexDirection: 'column',
                flexGrow: 1,
                flexBasis: '0%',
            },
        },
    };

    style.buttons = KEYS.reduce((buttons, keyName) => {
        buttons[keyName] = merge(config.buttons.default, config.buttons[keyName]);
        return buttons;
    }, {});

    return merge(config, style);
};
